/**
 * Pricing plan tiles with optional highlighted "most popular" lift.
 * Ported from ThemeMaker's {@code blocks/nav/PlansPricing}.
 *
 * <p>The highlighted plan sits 8px above its peers, gets a 2px brand
 * border with a brand-tinted shadow, and a filled (vs outlined) CTA.
 * Above its top edge sits a small pill {@code badge} (e.g. "MOST
 * POPULAR") if supplied.
 */
import { tx, asList } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { cardReset } from '../_shared/styles';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';

type Localized = string | Record<string, string>;

interface Plan {
  name?: Localized;
  tagline?: Localized;
  /** Pre-formatted: "$14" / "€12". The block doesn't do currency
   *  formatting since it has no locale context. */
  price?: string;
  pricePeriod?: Localized;
  badge?: Localized;
  features?: Localized[];
  ctaLabel?: Localized;
  ctaHref?: string;
  highlighted?: boolean;
}

export interface PlansPricingProps {
  title?: Localized;
  subtitle?: Localized;
  plans?: Plan[];
}

export function PlansPricing({ title, subtitle, plans }: PlansPricingProps) {
  const list = asList<Plan>(plans);
  if (list.length === 0) return null;
  return (
    <SectionShell title={tx(title)} subtitle={tx(subtitle)}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
          alignItems: 'stretch',
        }}
      >
        {list.map((plan, i) => {
          const hl = plan.highlighted ?? false;
          return (
            <Card
              key={i}
              className={hl ? 'ring-0' : undefined}
              style={{
                ...cardReset,
                position: 'relative',
                background: 'var(--color-surface-card, #fff)',
                ...(hl ? { border: '2px solid var(--color-brand-500, #722f37)' } : {}),
                borderRadius: 'var(--radius-md, 4px)',
                padding: 'var(--space-8, 32px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-4, 16px)',
                transform: hl ? 'translateY(-8px)' : 'none',
              }}
            >
              {plan.badge && (
                <div
                  style={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-brand-500, #722f37)',
                    color: 'var(--color-text-on-brand, #fff)',
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 800,
                    letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                    textTransform: 'uppercase',
                    padding: 'var(--space-1, 4px) var(--space-3, 12px)',
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tx(plan.badge)}
                </div>
              )}

              <div>
                <div
                  style={{
                    fontSize: 'clamp(22px, 2vw, 28px)',
                    fontWeight: 500,
                    fontStyle: 'italic',
                    fontFamily: 'var(--font-family-display, "DM Serif Display", Georgia, serif)',
                    color: 'var(--color-text-primary, #1a1614)',
                    lineHeight: 1.1,
                  }}
                >
                  {tx(plan.name)}
                </div>
                {plan.tagline && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: '13px',
                      color: 'var(--color-text-tertiary, #94867d)',
                      lineHeight: 1.45,
                    }}
                  >
                    {tx(plan.tagline)}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  marginTop: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 'clamp(36px, 3.6vw, 48px)',
                    fontWeight: 700,
                    fontFamily: 'var(--font-family-display, "DM Serif Display", Georgia, serif)',
                    color: 'var(--color-text-primary, #1a1614)',
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: 'var(--font-tracking-snug, -0.02em)',
                    lineHeight: 1,
                  }}
                >
                  {plan.price ?? '—'}
                </span>
                {plan.pricePeriod && (
                  <span
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-secondary, #5d4f47)',
                    }}
                  >
                    {tx(plan.pricePeriod)}
                  </span>
                )}
              </div>

              {plan.features && plan.features.length > 0 && (
                <ul
                  style={{
                    margin: 0,
                    padding: 0,
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    fontSize: 'var(--font-size-sm, 14px)',
                    color: 'var(--color-text-secondary, #5d4f47)',
                  }}
                >
                  {plan.features.map((f, j) => (
                    <li
                      key={j}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        lineHeight: 1.5,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          flexShrink: 0,
                          width: 18,
                          height: 18,
                          marginTop: 1,
                          borderRadius: '50%',
                          background: 'var(--color-brand-50, rgba(114,47,55,0.08))',
                          color: 'var(--color-brand-700, #5a242c)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span>{tx(f)}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div style={{ flex: 1 }} />

              {plan.ctaLabel && (
                <PortalLink
                  href={plan.ctaHref || '#'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-3-5, 14px) var(--space-6, 24px)',
                    background: hl ? 'var(--color-brand-500, #722f37)' : 'transparent',
                    color: hl
                      ? 'var(--color-text-on-brand, #fff)'
                      : 'var(--color-text-primary, #1a1614)',
                    border: hl
                      ? '1px solid var(--color-brand-500, #722f37)'
                      : '1px solid var(--color-text-primary, #1a1614)',
                    borderRadius: 'var(--radius-pill, 9999px)',
                    fontSize: 'var(--font-size-sm, 14px)',
                    fontWeight: 600,
                    textDecoration: 'none',
                    letterSpacing: 'var(--font-tracking-wide, 0.02em)',
                  }}
                >
                  {tx(plan.ctaLabel)}
                </PortalLink>
              )}
            </Card>
          );
        })}
      </div>
    </SectionShell>
  );
}
